<template>
  <!-- <div
    class="scroll-wrapper"
    :style="{ height: props.height - props.navHeight - props.tabHeight + 'px' }"
  > -->
  <div class="scroll-wrapper">
    <div class="scroll-content">
      <div class="pull-down-slot">
        <slot v-if="isShowPullDownTips" name="pull-down-tips"></slot>
        <slot v-if="isShowPullDownLoad" name="pull-down-loading"></slot>
      </div>
      <slot name="scroll-content"></slot>
      <div class="pull-up-tips" v-if="isShowPullUpTips">
        <slot name="pull-up-tips"></slot>
      </div>
      <div class="pull-up-loading" v-if="isShowPullUpload">
        <slot name="pull-up-loading"></slot>
      </div>
      <div class="pull-up-end-slot" v-if="isShowPullUpEnd">
        <slot name="pull-up-end"></slot>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
  const DEFAULT_OPTIONS = {
    observeDOM: true,
    click: true,
    probeType: 1,
    scrollbar: false,
    bounce: true,
    mouseWheel: true,
  };
</script>
<script setup lang="ts">
  /**
   * @evnet {Function} pullingUp(callback) 滚动到底部事件, 用于上拉加载, 需要释放事件
   * @evnet {Function} pullingDown(callback) 滚动到顶部下拉事件, 用于下拉刷新
   */
  import { onMounted, ref, computed, onUpdated } from 'vue';
  import BScroll from '@better-scroll/core';
  import PullDown from '@better-scroll/pull-down';
  import PullUp from '@better-scroll/pull-up';
  import { EaseItem } from '@better-scroll/shared-utils';

  // 扩展下拉刷新的能力
  BScroll.use(PullDown);
  // 扩展上拉加载的能力
  BScroll.use(PullUp);

  const props = defineProps({
    options: {
      type: Object,
      default: () => {
        return DEFAULT_OPTIONS;
      },
    },
    // 滑动块高度(默认页面高度)
    height: {
      type: Number,
      default: document.documentElement.clientHeight || document.body.clientHeight,
    },
    // 头部导航高度(默认46)
    navHeight: {
      type: Number,
      default: 46,
    },
    // 底部导航高度(默认50)
    tabHeight: {
      type: Number,
      default: 50,
    },
    // 是否开启上拉(底部)刷新
    pullUpLoad: {
      type: Boolean,
      default: false,
    },
    // 是否拖动到底了
    pullUpLoadEnd: {
      type: Boolean,
      default: false,
    },
    // 是否开启下拉(头部)刷新
    pullDownRefresh: {
      type: Boolean,
      default: false,
    },
    // 下拉停止高度
    pullDownRefreshTopHeight: {
      type: Number,
      default: 40,
    },
  });
  const emit = defineEmits(['pullup', 'pulldown', 'scroll']);

  // 顶部是否处在加载状态
  const isPullDownLoad = ref(false);
  // 是否展示顶部提示插糟
  const isShowPullDownTips = computed(() => {
    return props.pullDownRefresh && !isPullDownLoad.value;
  });

  // 是否展示顶部加载插糟
  const isShowPullDownLoad = computed(() => {
    return props.pullDownRefresh && isPullDownLoad.value;
  });

  // 底部是否处在加载状态
  const isPullUpload = ref(false);
  // 是否展示底部提示插糟
  const isShowPullUpTips = computed(() => {
    return props.pullUpLoad && !isPullUpload.value && !props.pullUpLoadEnd;
  });
  // 是否展示底部提示插糟
  const isShowPullUpload = computed(() => {
    return props.pullUpLoad && isPullUpload.value && !props.pullUpLoadEnd;
  });
  // 是否展示底部到底插糟
  const isShowPullUpEnd = computed(() => {
    return props.pullUpLoad && props.pullUpLoadEnd;
  });

  const scrollTo = ref<((...args: any[]) => void) | null>(null);
  const backTop = ref<(() => void) | null>(null); // 回到顶部
  const scrollRefresh = ref<(() => void) | null>(null); // 刷新重新计算
  defineExpose({
    scrollTo,
    backTop,
    scrollRefresh,
  });

  // 是否展示底部
  onMounted(() => {
    // 初始化滚动条
    const scrollEl = document.querySelector<HTMLElement>('.scroll-wrapper');
    if (!scrollEl) {
      return false;
    }

    const pullDownRefreshOpts = props.pullDownRefresh && {
      stop: props.pullDownRefreshTopHeight,
    };

    const u = navigator.userAgent;
    const isXiaomi = u.indexOf('XiaoMi') > -1; // 小米手机
    const isIOS = !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/); // ios
    const options = {
      ...DEFAULT_OPTIONS,
      pullDownRefresh: pullDownRefreshOpts as any,
      pullUpLoad: props.pullUpLoad as any,
      ...props.options,
      useTransition: !(isIOS && !isXiaomi),
    };

    const scroll = new BScroll(scrollEl, options);
    let refreshTimer: number | undefined = undefined;
    const forceUpdate = (dirty = false) => {
      if (props.pullDownRefresh && isPullDownLoad.value) {
        isPullDownLoad.value = false;
      } else if (props.pullUpLoad && isPullUpload.value) {
        isPullUpload.value = false;
        scroll.finishPullUp();
        dirty && scroll.refresh();
      } else {
        scroll.refresh();
      }
    };
    const refresh = () => {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        forceUpdate(true);
      }, 0);
    };

    scrollTo.value = (x: number, y: number, time?: number | undefined, easing?: EaseItem | undefined) => {
      scroll.scrollTo(x, y, time, easing);
      refresh();
    };

    backTop.value = () => {
      scroll.scrollTo(0, 0);
      setTimeout(refresh, 1500);
    };

    scrollRefresh.value = () => {
      scroll.refresh();
    };

    // 派发滚动到底部事件, 用于上拉加载
    props.pullUpLoad &&
      scroll.on('pullingUp', () => {
        isPullUpload.value = true;
        emit('pullup', () => {
          scroll.finishPullUp();
          scroll.refresh();
          isPullUpload.value = false;
        });
      });
    // 派发滚动到顶部下拉事件, 用于下拉刷新
    props.pullDownRefresh &&
      scroll.on('pullingDown', () => {
        isPullDownLoad.value = true;
        emit('pulldown', () => {
          scroll.finishPullDown();
          scroll.refresh();
          isPullDownLoad.value = false;
        });
      });
    // 派发滚动事件
    scroll.on('scroll', (pos: { x: number; y: number }) => {
      emit('scroll', pos);
    });
  });
</script>
<style lang="less">
  .scroll-wrapper {
    height: 100%;
    overflow: hidden;
  }

  .pull-down-slot {
    position: absolute;
    overflow: hidden;
    transform: translateY(-100%) translateZ(0);
  }

  .pull-up-end-slot {
    display: flex;
    justify-content: center;
    height: 0.6rem;
    line-height: 0.6rem;
    font-size: 0.24rem;
    font-weight: 400;
    color: #999;
  }
</style>
